<template>
    <div class="modal-card card paice-confirm">
        <!-- The title carries the whole question; anything that identifies
             what it is about belongs in the body, where there is room to say
             it exactly. -->
        <div class="card-header">
            <h4 class="card-header-title mb-0">{{ modalData.title }}</h4>
            <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"
                :disabled="working"></button>
        </div>

        <div class="card-body">
            <!-- The one sentence someone reads if they read nothing else. -->
            <p class="paice-confirm-lede mb-0">{{ modalData.lede }}</p>

            <!-- What is being changed, and — as the last row — the change
                 itself, before → after in the same colours the settings cards
                 use, so its direction is legible without reading it. -->
            <dl v-if="facts.length || shift" class="paice-confirm-facts">
                <div v-for="fact in facts" :key="fact.label">
                    <dt>{{ fact.label }}</dt>
                    <dd>
                        <code v-if="fact.mono" class="paice-confirm-mono">{{ fact.value }}</code>
                        <template v-else>{{ fact.value }}</template>
                    </dd>
                </div>
                <div v-if="shift">
                    <dt>{{ shift.label || 'Change' }}</dt>
                    <dd class="paice-confirm-shift">
                        <span class="paice-confirm-state"
                            :class="`paice-confirm-state-${shift.from.tone || 'neutral'}`">
                            <i v-if="shift.from.icon" class="fa-solid" :class="shift.from.icon"
                                aria-hidden="true"></i>
                            {{ shift.from.label }}
                        </span>
                        <i class="fa-solid fa-arrow-right paice-confirm-arrow" aria-hidden="true"></i>
                        <span class="paice-confirm-state"
                            :class="`paice-confirm-state-${shift.to.tone || 'neutral'}`">
                            <i v-if="shift.to.icon" class="fa-solid" :class="shift.to.icon"
                                aria-hidden="true"></i>
                            {{ shift.to.label }}
                        </span>
                    </dd>
                </div>
            </dl>

            <!-- What actually happens, one consequence per line. Anything that
                 loses data is flagged; anything that survives says so, because
                 "is my key gone too?" is what this dialog gets asked. -->
            <ul v-if="points.length" class="paice-confirm-points">
                <li v-for="(point, i) in points" :key="i" :class="`paice-confirm-point-${point.tone || 'neutral'}`">
                    <i class="fa-solid" :class="point.icon || 'fa-circle-info'" aria-hidden="true"></i>
                    <span>{{ point.text }}</span>
                </li>
            </ul>

            <div v-if="modalData.scope" class="paice-confirm-scope">
                <i class="fa-solid" :class="modalData.scopeIcon || 'fa-users'" aria-hidden="true"></i>
                <span>{{ modalData.scope }}</span>
            </div>
        </div>

        <div class="card-footer paice-confirm-footer">
            <span class="paice-confirm-note">
                <template v-if="error">
                    <span class="text-danger">
                        <i class="fa-solid fa-circle-exclamation me-1"></i>{{ error }}
                    </span>
                </template>
                <template v-else-if="modalData.irreversible">
                    <i class="fa-solid fa-triangle-exclamation me-1"></i>This cannot be undone.
                </template>
            </span>

            <div class="d-flex gap-2">
                <button ref="cancel" class="btn btn-secondary" :disabled="working" @click="cancelClick">
                    {{ modalData.cancelText || 'Cancel' }}
                </button>
                <button ref="confirm" class="btn" :class="confirmClass" :disabled="working" @click="okClick">
                    <span v-if="working" class="spinner-border spinner-border-sm me-1" role="status"
                        style="width: 1em; height: 1em;">
                        <span class="visually-hidden">Working</span>
                    </span>
                    {{ modalData.confirmText || 'Confirm' }}
                </button>
            </div>
        </div>
    </div>
</template>

<script>
/**
 * The confirmation dialog for PAICE settings — MCP servers, the agent's prompt
 * and model, the context layer.
 *
 * These changes share a shape the generic `Confirm` modal cannot express: the
 * change has a direction (in use → off, Allow → Ask, client settings → service
 * defaults), it usually lands on everyone in the client rather than on the
 * person clicking, and what it does *not* touch is as much of the answer as
 * what it does. So the caller passes the parts and this lays them out, instead
 * of flattening the lot into one paragraph of HTML:
 *
 *   title          the question, and the whole of the header
 *   lede           the one sentence someone reads if they read nothing else
 *   facts          [{ label, value, mono }] — which thing is being changed
 *   shift          { label?, from, to }, each side { label, tone, icon }
 *   points         [{ icon, tone, text }] — one consequence per line, tone
 *                  'danger' for what is lost, 'keep' for what survives
 *   scope          who else is affected, as a footnote under a rule
 *   irreversible   turns on the "This cannot be undone." note in the footer
 *   tone           'danger' | 'warning' | 'primary' — colours the action and
 *                  decides which button takes initial focus
 *
 * The header is only the question. Anything that identifies *which* thing is
 * being changed goes in the fact rows, where there is room to print an endpoint
 * in full rather than truncate it under a title.
 *
 * `confirm` may return a promise; the dialog stays open with the button
 * spinning until it settles, and closes itself afterwards — so a caller must
 * not also hide the modal. A rejection is caught and shown here rather than
 * left as a stuck spinner, even though the settings call sites all report their
 * own errors and resolve either way.
 */
const TONE_BUTTON = {
    danger: 'btn-danger',
    warning: 'btn-warning',
    primary: 'btn-primary',
};

export default {
    name: 'PaiceConfirm',
    props: {
        modalData: {
            type: Object,
            default: () => ({}),
        },
    },
    data() {
        return {
            working: false,
            error: '',
        };
    },
    computed: {
        tone() {
            return TONE_BUTTON[this.modalData?.tone] ? this.modalData.tone : 'primary';
        },
        /** Reference detail — which server, which model — as label/value rows. */
        facts() {
            return (this.modalData?.facts || []).filter((f) => f && f.label && f.value);
        },
        confirmClass() {
            return this.modalData?.confirmClass || TONE_BUTTON[this.tone];
        },
        shift() {
            const shift = this.modalData?.shift;
            return shift?.from && shift?.to ? shift : null;
        },
        points() {
            return (this.modalData?.points || []).filter((p) => p && p.text);
        },
    },
    mounted() {
        // Keyboard focus lands on the safe answer for anything destructive, and
        // on the action otherwise — pressing Enter on reflex should not delete
        // a server or wipe a prompt, but it may as well enable one.
        this.$nextTick(() => {
            const target = this.tone === 'danger' ? this.$refs.cancel : this.$refs.confirm;
            if (target) target.focus();
        });
    },
    methods: {
        cancelClick() {
            if (typeof this.modalData?.cancel === 'function') this.modalData.cancel();
            this.$store.dispatch('hideModal');
        },
        async okClick() {
            if (typeof this.modalData?.confirm !== 'function') {
                this.$store.dispatch('hideModal');
                return;
            }
            this.working = true;
            this.error = '';
            try {
                await this.modalData.confirm();
                this.$store.dispatch('hideModal');
            } catch (e) {
                this.error = e?.message || 'That did not go through. Try again.';
                this.working = false;
            }
        },
    },
};
</script>

<style scoped>
.paice-confirm-lede {
    font-size: 0.85rem;
    color: #12263f;
}

/* Same label/value block the settings page uses for a server's configuration
   — this dialog is about something on that page, so it should look like it. */
.paice-confirm-facts {
    margin: 1rem 0 0;
    padding: 0.15rem 0;
    border: 1px solid #edf2f9;
    border-radius: 0.375rem;
    background: #f9fbfd;
}

.paice-confirm-facts > div {
    display: flex;
    align-items: baseline;
    gap: 0.75rem;
    padding: 0.45rem 0.85rem;
}

.paice-confirm-facts > div + div {
    border-top: 1px solid #edf2f9;
}

.paice-confirm-facts dt {
    /* Wide enough for the longest label in use ("System prompt") to stay on
       one line, so the values still line up in a column. */
    flex: 0 0 7rem;
    font-size: 0.65rem;
    font-weight: 600;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: #95aac9;
}

.paice-confirm-facts dd {
    margin: 0;
    min-width: 0;
    font-size: 0.78rem;
    color: #12263f;
    word-break: break-word;
}

.paice-confirm-mono {
    font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
    font-size: 0.72rem;
    color: #6e84a3;
    background: none;
    padding: 0;
    word-break: break-all;
}

.paice-confirm-shift {
    display: flex;
    align-items: center;
    gap: 0.6rem;
    flex-wrap: wrap;
}

.paice-confirm-state {
    display: inline-flex;
    align-items: center;
    gap: 0.35rem;
    padding: 0.2rem 0.6rem;
    border-radius: 999px;
    font-size: 0.72rem;
    font-weight: 600;
}

.paice-confirm-state i {
    font-size: 0.65rem;
}

.paice-confirm-state-ready,
.paice-confirm-state-allow {
    background: #e6f6ee;
    color: #00854a;
}

.paice-confirm-state-ask,
.paice-confirm-state-attention {
    background: #fdf3e7;
    color: #b56a1d;
}

.paice-confirm-state-deny,
.paice-confirm-state-blocked {
    background: #fdeaed;
    color: #c9314a;
}

.paice-confirm-state-off,
.paice-confirm-state-neutral {
    background: #edf2f9;
    color: #6e84a3;
}

.paice-confirm-arrow {
    font-size: 0.7rem;
    color: #b1c2d9;
}

.paice-confirm-points {
    list-style: none;
    margin: 1rem 0 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: 0.55rem;
}

.paice-confirm-points li {
    display: flex;
    align-items: flex-start;
    gap: 0.55rem;
    font-size: 0.78rem;
    line-height: 1.45;
    color: #12263f;
}

.paice-confirm-points i {
    margin-top: 0.2rem;
    width: 0.9rem;
    flex-shrink: 0;
    text-align: center;
    font-size: 0.72rem;
    color: #95aac9;
}

/* Losing something is worth colouring; keeping something is worth saying but
   not worth shouting, so it stays muted. */
.paice-confirm-point-danger i {
    color: #e63757;
}

.paice-confirm-point-keep,
.paice-confirm-point-keep i {
    color: #6e84a3;
}

.paice-confirm-scope {
    display: flex;
    align-items: flex-start;
    gap: 0.5rem;
    margin-top: 1.1rem;
    padding-top: 0.85rem;
    border-top: 1px solid #edf2f9;
    font-size: 0.72rem;
    color: #95aac9;
}

.paice-confirm-scope i {
    margin-top: 0.15rem;
    font-size: 0.7rem;
}

.paice-confirm-footer {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
}

.paice-confirm-note {
    font-size: 0.72rem;
    color: #95aac9;
    overflow-wrap: break-word;
}
</style>

<style lang="scss">
@use '@/theme/scss/_user-variables.scss' as *;

/* Unscoped: `body.dark-mode` sits on an ancestor of the modal root, which a
   scoped selector cannot reach. The card, buttons and close control are
   covered globally in _user.scss — only the names authored here need
   restating. */
body.dark-mode {

    .paice-confirm-note,
    .paice-confirm-scope,
    .paice-confirm-arrow {
        color: $dark-text-muted !important;
    }

    .paice-confirm-lede,
    .paice-confirm-points li {
        color: $dark-text-primary !important;
    }

    .paice-confirm-points i {
        color: $dark-text-muted !important;
    }

    .paice-confirm-point-keep,
    .paice-confirm-point-keep i {
        color: $dark-text-secondary !important;
    }

    .paice-confirm-facts {
        background: $dark-body-bg !important;
        border-color: $dark-border-color !important;
    }

    .paice-confirm-facts > div + div {
        border-top-color: $dark-border-color !important;
    }

    .paice-confirm-facts dt {
        color: $dark-text-muted !important;
    }

    .paice-confirm-facts dd {
        color: $dark-text-primary !important;
    }

    .paice-confirm-mono {
        color: $dark-text-secondary !important;
    }

    .paice-confirm-scope {
        border-top-color: $dark-border-color !important;
    }

    /* Translucent tints: the light theme's solid pastels read as bright blocks
       against a near black card. */
    .paice-confirm-state-ready,
    .paice-confirm-state-allow {
        background: rgba(0, 217, 126, 0.14) !important;
        color: #5fd3a3 !important;
    }

    .paice-confirm-state-ask,
    .paice-confirm-state-attention {
        background: rgba(217, 130, 43, 0.16) !important;
        color: #e0a458 !important;
    }

    .paice-confirm-state-deny,
    .paice-confirm-state-blocked {
        background: rgba(230, 55, 87, 0.16) !important;
        color: #f5808f !important;
    }

    .paice-confirm-state-off,
    .paice-confirm-state-neutral {
        background: $dark-elevated-bg !important;
        color: $dark-text-secondary !important;
    }

    .paice-confirm-point-danger i {
        color: #f5808f !important;
    }
}
</style>
